import { ClockCircleOutlined } from '@ant-design/icons';
import { formatDisplayTime, parseApiTime } from '@hq/utils';
import { Tag, Tooltip } from 'antd';
import { useEffect, useState } from 'react';

/** 临期阈值：剩余 ≤ 2 小时转橙色（应急 2h 到货的硬指标，需提前预警）。 */
const WARN_MINUTES = 120;

interface SlaBadgeProps {
  /** 后端 `sla_deadline`（应急 2h / 加急 24h / 常规 48h）。 */
  deadline?: string | null;
  /** 已结束的单据不再倒计时（如已签收/已关闭）。 */
  finished?: boolean;
  /** 完成后展示的文案，如「已签收」。 */
  finishedText?: string;
}

/** 每 30 秒刷新一次当前时间：倒计时要自己走，不能靠用户刷新页面。 */
function useNow(active: boolean): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

function formatRemain(minutes: number): string {
  const abs = Math.abs(minutes);
  const days = Math.floor(abs / (60 * 24));
  const hours = Math.floor((abs % (60 * 24)) / 60);
  const mins = abs % 60;
  if (days > 0) return `${days}天${hours}小时`;
  if (hours > 0) return `${hours}小时${mins}分`;
  return `${mins}分`;
}

/**
 * 申领/配送时效徽标（docs/01 §5 P3 链路：应急 2 小时到科）：
 * 正常灰、临近橙、超时红，悬停显示完整截止时间。
 */
export function SlaBadge({ deadline, finished, finishedText }: SlaBadgeProps) {
  const active = Boolean(deadline) && !finished;
  const now = useNow(active);

  if (finished) {
    return finishedText ? <Tag color="green">{finishedText}</Tag> : null;
  }
  if (!deadline) return <span>-</span>;

  const due = parseApiTime(deadline);
  if (!due) return <span>-</span>;

  const minutes = Math.round((due.getTime() - now.getTime()) / 60_000);
  const overdue = minutes < 0;
  const warning = !overdue && minutes <= WARN_MINUTES;
  const color = overdue ? 'red' : warning ? 'orange' : 'default';
  const text = overdue ? `超时 ${formatRemain(minutes)}` : `剩余 ${formatRemain(minutes)}`;

  return (
    <Tooltip title={`应到时间 ${formatDisplayTime(due)}`}>
      <Tag color={color} icon={<ClockCircleOutlined />}>
        {text}
      </Tag>
    </Tooltip>
  );
}

/** 非组件场景（如表格排序/统计）需要剩余分钟数时复用。 */
export function slaMinutesLeft(deadline?: string | null): number | null {
  const due = parseApiTime(deadline ?? undefined);
  if (!due) return null;
  return Math.round((due.getTime() - Date.now()) / 60_000);
}
